import {
  RedirectToSignIn,
  SignedIn,
  SignedOut,
  useUser,
  ClerkLoading,
  ClerkLoaded,
} from "@clerk/clerk-react";
import { Outlet, useLocation } from "react-router-dom";
import posthog from "posthog-js";
import { useEffect } from "react";

function FullscreenLoginSpinner() {
  return (
    <div className="flex h-[100dvh] w-full items-center justify-center">
      <div>
        <h2 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
          Secure Access Checklist
        </h2>
        <ul className="max-w-md list-inside space-y-2 text-gray-500 dark:text-gray-400">
          <li className="flex items-center">
            <svg
              className="me-2 h-4 w-4 flex-shrink-0 text-green-500 dark:text-green-400"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z" />
            </svg>
            HTTPS via AES-256 encryption
          </li>
          <li className="flex items-center">
            <svg
              className="me-2 h-4 w-4 flex-shrink-0 text-green-500 dark:text-green-400"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z" />
            </svg>
            Connected to EU data region
          </li>
          <li className="flex items-center">
            <div role="status">
              <svg
                aria-hidden="true"
                className="me-2 h-4 w-4 animate-spin fill-blue-600 text-gray-200 dark:text-gray-600"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="currentColor"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentFill"
                />
              </svg>
              <span className="sr-only">Loading...</span>
            </div>
            Authentication
          </li>
        </ul>
      </div>
    </div>
  );
}

export default function EnsureAuthorized() {
  const location = useLocation();
  const { isLoaded, isSignedIn, user } = useUser();

  useEffect(() => {
    if (isLoaded) {
      if (isSignedIn)
        posthog.identify(user.id, {
          name: user.fullName,
          email: user.primaryEmailAddress?.emailAddress,
        });
      else posthog.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, isSignedIn, user?.id]);

  useEffect(() => {
    if (isLoaded) {
      if (isSignedIn) {
        window.$crisp.push(["do", "chat:show"]);
        // Set user data for Crisp
        if (user?.fullName)
          window.$crisp.push(["set", "user:nickname", user.fullName]);
        if (user?.primaryEmailAddress?.emailAddress)
          window.$crisp.push([
            "set",
            "user:email",
            user.primaryEmailAddress.emailAddress,
          ]);
        if (user?.primaryPhoneNumber?.phoneNumber)
          window.$crisp.push([
            "set",
            "user:phone",
            user.primaryPhoneNumber?.phoneNumber,
          ]);
        window.$crisp.push([
          "set",
          "session:data",
          ["user_id", user?.id],
        ]);
      } else window.$crisp.push(["do", "chat:hide"]);
    }
  }, [
    isLoaded,
    isSignedIn,
    user?.id,
    user?.primaryEmailAddress?.emailAddress,
    user?.primaryPhoneNumber?.phoneNumber,
    user?.fullName,
  ]);
  return (
    <div>
      <ClerkLoading>
        <FullscreenLoginSpinner />
      </ClerkLoading>
      <ClerkLoaded>
        <SignedOut>
          <FullscreenLoginSpinner />
          <RedirectToSignIn afterSignInUrl={location.pathname} />
        </SignedOut>
        <SignedIn>
          <Outlet />
        </SignedIn>
      </ClerkLoaded>
    </div>
  );
}
